import React from 'react';
import { motion } from 'framer-motion';

/**
 * Props for the TrendItem component
 */
interface TrendItemProps {
  /**
   * The search term text to display in the card
   * Default: "Loading..."
   */
  content?: string;
  
  /**
   * Callback function triggered when the item is clicked
   * This is used to handle user selection
   */
  onClick?: () => void;
  
  /**
   * Whether this item has been selected by the user
   * Changes appearance when selected
   * Default: false
   */
  isSelected?: boolean;
  
  /**
   * Whether this selection was correct
   * Only relevant when isSelected is true
   * Default: false
   */
  isCorrect?: boolean;
  
  /**
   * Whether clicking is disabled on this item
   * Used to prevent changing answers after selection
   * Default: false
   */
  isDisabled?: boolean;
}

/**
 * TrendItem: Interactive card component for displaying a search term
 * 
 * This component serves as the main interactive element in the game.
 * It displays a search term as a clickable card that provides visual
 * feedback based on the user's interaction.
 * 
 * The component demonstrates:
 * - Using Framer Motion for animations and interactions
 * - Conditional styling based on component state
 * - Click handling with validation
 * - Managing local UI state (drag detection)
 */
const TrendItem: React.FC<TrendItemProps> = ({ 
  content = "Loading...", 
  onClick, 
  isSelected = false, 
  isCorrect = false, 
  isDisabled = false
}) => {
  /**
   * Local state to track whether this item is being dragged
   * This prevents accidental clicks when dragging
   */
  const [isDragging, setIsDragging] = React.useState(false);

  /**
   * Determine the background color based on component state
   * 
   * Color coding:
   * - Blue: Default, not selected
   * - Green: Selected and correct
   * - Red: Selected but incorrect
   * - Gray: Not selected but disabled (after another selection)
   * 
   * @returns CSS color string for the background
   */
  const getBackgroundColor = () => {
    if (isSelected) {
      // If this item is selected, color is based on correctness
      return isCorrect ? '#4CAF50' : '#FF5252';
    }
    // If not selected, color depends on whether it's disabled
    return isDisabled ? '#9E9E9E' : '#4A90E2';
  };

  return (
    <motion.div
      // Animations for better interactivity
      whileTap={{ scale: 0.95 }} // Shrink slightly when pressed
      whileHover={{ scale: 1.05 }} // Grow slightly on hover
      
      // Track drag state to prevent click after drag
      onDragStart={() => setIsDragging(true)}
      onDragEnd={() => setIsDragging(false)}
      
      // Click handler with validation
      onClick={() => {
        // Only trigger click if not dragging and not disabled
        if (!isDragging && !isDisabled && onClick) {
          onClick();
        }
      }}
      
      // Card styling
      style={{
        // Size and layout
        width: '170px',
        height: '80px',
        padding: '0 10px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        
        // Appearance
        backgroundColor: getBackgroundColor(),
        borderRadius: '8px',
        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
        color: 'white',
        fontWeight: 'bold',
        opacity: isDisabled ? 0.7 : 1,
        
        // Behavior
        cursor: isDisabled ? 'default' : 'pointer',
        userSelect: 'none',
        transition: 'background-color 0.3s ease'
      }}
    >
      {content}
    </motion.div>
  );
};

export default TrendItem;